/**
 * Gate 2 wizard + execution restore for Gate 3 → Gate 2 navigation.
 *
 * - Wizard (steps, selection, decision, builds): sessionStorage per project.
 * - Scorecard: prefer API run scores; fall back to session metricResults when
 *   the latest run has evidence but no persisted scores yet.
 */

const storageKey = (projectId) => `validaite.gate2.session.${projectId}`;

/**
 * Merge browser session with server `projects.gate2_data` for restore.
 * Session wins when both have scorecard rows (same tab); otherwise use DB.
 */
export function coalesceGate2Restore(session, persisted) {
  const fromDb = persisted && typeof persisted === "object" ? persisted : null;
  const fromSession = session && typeof session === "object" ? session : null;
  if (!fromDb) return fromSession;
  if (!fromSession) return fromDb;

  const sessionHas =
    Array.isArray(fromSession.metricResults) && fromSession.metricResults.length > 0;
  const dbHas = Array.isArray(fromDb.metricResults) && fromDb.metricResults.length > 0;
  if (sessionHas && !dbHas) return fromSession;
  if (dbHas && !sessionHas) return fromDb;

  const sessionRun = fromSession.runId != null ? String(fromSession.runId) : "";
  const dbRun = fromDb.runId != null ? String(fromDb.runId) : "";
  if (dbRun && sessionRun && dbRun !== sessionRun) return fromDb;

  return fromSession;
}

export function readGate2Session(projectId) {
  if (!projectId) return null;
  try {
    const raw = sessionStorage.getItem(storageKey(projectId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export function writeGate2Session(projectId, state) {
  if (!projectId || !state) return;
  try {
    sessionStorage.setItem(storageKey(projectId), JSON.stringify(state));
  } catch {
    /* quota / private mode */
  }
}

/** True when listRuns attached score rows for this run. */
export function runHasScores(run) {
  return Array.isArray(run?.scores) && run.scores.length > 0;
}

/**
 * Pick which execution run to hydrate on load.
 * 1) Saved run id if that run has scores
 * 2) Newest run with scores (any index in the list)
 * 3) Saved run id (evidence-only / in-progress)
 * 4) Newest run in the list
 */
export function chooseRunIdForRestore(runs = [], savedRunId = null) {
  if (!Array.isArray(runs) || !runs.length) {
    return savedRunId != null ? String(savedRunId) : null;
  }
  const byId = new Map(runs.map((r) => [String(r.id), r]));
  const savedKey = savedRunId != null ? String(savedRunId) : "";
  const savedRun = savedKey ? byId.get(savedKey) : null;

  if (savedRun && runHasScores(savedRun)) return savedRun.id;

  const withScores = runs.find(runHasScores);
  if (withScores?.id) return withScores.id;

  if (savedRun?.id) return savedRun.id;

  return runs[0]?.id ?? null;
}

export function gate2SessionSnapshot(state = {}) {
  const builds =
    state.builds instanceof Map
      ? [...state.builds.entries()].map(([id, build]) => ({
          id: String(id),
          version: build?.version ?? "",
          environment: build?.environment ?? "",
          commit: build?.commit ?? "",
        }))
      : Array.isArray(state.builds)
        ? state.builds
        : [];

  const completedStepIds =
    state.completedStepIds instanceof Set
      ? [...state.completedStepIds]
      : Array.isArray(state.completedStepIds)
        ? state.completedStepIds
        : [];

  const selectedByMetric =
    state.selectedByMetric instanceof Set
      ? [...state.selectedByMetric]
      : Array.isArray(state.selectedByMetric)
        ? state.selectedByMetric
        : [];

  const selectedBySuite =
    state.selectedBySuite instanceof Set
      ? [...state.selectedBySuite]
      : Array.isArray(state.selectedBySuite)
        ? state.selectedBySuite
        : [];

  return {
    stepId: state.stepId,
    scopeMode: state.scopeMode,
    selectedByMetric,
    selectedBySuite,
    completedStepIds,
    decisionVote: state.decisionVote,
    decisionRationale: state.decisionRationale,
    decisionAttestation: state.decisionAttestation,
    runId: state.runId ?? null,
    metricResults: Array.isArray(state.metricResults) ? state.metricResults : [],
    builds,
    assignPhase: state.assignPhase === "execute" ? "execute" : "review",
  };
}

export function buildsFromSession(entries = []) {
  const map = new Map();
  if (!Array.isArray(entries)) return map;
  for (const row of entries) {
    if (row?.id == null) continue;
    map.set(row.id, {
      version: row.version || "",
      environment: row.environment || "",
      commit: row.commit || "",
    });
  }
  return map;
}

export function retrievalMapFromRunResults(results = []) {
  const map = new Map();
  for (const row of results) {
    const id = row?.testCaseId;
    if (!id) continue;
    const chunks = Array.isArray(row.chunks) ? row.chunks : [];
    const answer = String(row.actualOutput || "").trim();
    let outcome = row.outcome;
    if (!["retrieved", "empty", "failed", "skipped"].includes(outcome)) {
      outcome = answer || chunks.length ? "retrieved" : "failed";
    }
    map.set(id, {
      prompt: row.prompt || "",
      outcome,
      chunks,
      answer,
      traces: row.traces || [],
      evidence: answer ? { actualOutput: answer, httpStatus: row.statusCode ?? null } : null,
      extras: {},
      statusCode: row.statusCode ?? null,
      latencyMs: row.latencyMs ?? null,
      askOk: Boolean(answer),
      askError: null,
      matchCount: row.matchCount ?? 0,
      usableCount: row.usableCount ?? 0,
      error: row.error || null,
    });
  }
  return map;
}

export function metricResultsFromScores(scores = []) {
  return (scores || []).map((s, i) => ({
    key: s.suiteId ? `${s.suiteId}:${s.metricId}` : s.metricId || String(i),
    metricId: s.metricId,
    metricName: s.metricName,
    suiteId: s.suiteId || null,
    suiteName: s.metricName,
    threshold: s.threshold,
    direction: s.direction,
    unit: s.unit,
    value: s.value,
    passed: s.passed,
    provider: s.provider || null,
    n: s.sampleCount,
    perCase: s.perCase || [],
    error: s.error || null,
  }));
}

export function selectionFromRun({ scores = [], results = [], metrics = [], testCases = [] } = {}) {
  const byMetric = new Set();
  const bySuite = new Set();
  for (const s of scores) {
    if (s.suiteId) bySuite.add(String(s.suiteId));
    const row = metrics.find((m) => m.metricId === s.metricId || m.formulaFn === s.metricId);
    if (row?.id != null) byMetric.add(String(row.id));
  }
  const casesById = new Map(testCases.map((tc) => [String(tc.id), tc]));
  for (const r of results) {
    const tc = casesById.get(String(r.testCaseId));
    if (tc?.metricId != null) byMetric.add(String(tc.metricId));
  }
  return { byMetric, bySuite };
}
